import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Phone, Mail, Clock, ArrowRight, MessageCircle, MapPin, Download } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { SERVICE_OPTIONS, HORARIO } from "../data";
import { downloadVCard } from "../lib/vcard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "rounded-none border-border bg-white focus-visible:ring-1 focus-visible:ring-electric focus-visible:border-electric h-12";

const Field = ({ label, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <Label className="text-xs uppercase tracking-wider font-bold text-ink/70">{label}</Label>
    {children}
  </div>
);

const SubmitBtn = ({ loading, children, testid }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testid}
    className="group inline-flex items-center justify-center gap-3 bg-ink text-white font-semibold px-8 py-4 hover:bg-electric transition-colors duration-300 disabled:opacity-60 w-full sm:w-auto"
  >
    {loading ? <Loader2 className="animate-spin" size={18} /> : null}
    {children}
    {!loading && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
  </button>
);

export const BookingSection = () => {
  const [loading, setLoading] = useState({ contact: false, appointment: false, quote: false });
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [appt, setAppt] = useState({ name: "", email: "", phone: "", service: "", date: "", time: "", notes: "" });
  const [quote, setQuote] = useState({ name: "", email: "", phone: "", service: "", address: "", description: "" });

  const post = async (key, url, body, reset) => {
    setLoading((s) => ({ ...s, [key]: true }));
    try {
      await axios.post(`${API}/${url}`, body);
      toast.success("¡Enviado! Te responderemos muy pronto.");
      reset();
    } catch (e) {
      toast.error("No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setLoading((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <section id="contacto" className="bg-background py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-electric">Contacto</span>
            <h2 className="font-heading font-bold text-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-5">
              Hablemos de tu proyecto.
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Pide tu presupuesto gratuito, reserva una visita o escríbenos. Respondemos
              a cada mensaje personalmente.
            </p>
            <div className="mt-10 space-y-5">
              <a href="tel:689634999" className="flex items-center gap-4 text-ink group" data-testid="contact-phone">
                <span className="w-11 h-11 grid place-items-center bg-muted group-hover:bg-electric group-hover:text-white transition-colors"><Phone size={18} /></span>
                <span className="font-semibold">689 634 999</span>
              </a>
              <a href="https://wa.me/34628328285" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-ink group" data-testid="contact-whatsapp">
                <span className="w-11 h-11 grid place-items-center bg-muted group-hover:bg-[#25D366] group-hover:text-white transition-colors"><MessageCircle size={18} /></span>
                <span className="font-semibold">WhatsApp · 628 328 285</span>
              </a>
              <a href="mailto:hondallopezivan@gmail.com" className="flex items-center gap-4 text-ink group" data-testid="contact-email">
                <span className="w-11 h-11 grid place-items-center bg-muted group-hover:bg-electric group-hover:text-white transition-colors"><Mail size={18} /></span>
                <span className="font-semibold">hondallopezivan@gmail.com</span>
              </a>
              <div className="flex items-start gap-4 text-ink">
                <span className="w-11 h-11 shrink-0 grid place-items-center bg-muted"><MapPin size={18} /></span>
                <div>
                  <div className="font-semibold">Renedo de Piélagos, Cantabria</div>
                  <div className="text-sm text-muted-foreground">Servicio a domicilio en toda Cantabria</div>
                </div>
              </div>
              <div className="flex items-start gap-4 text-ink">
                <span className="w-11 h-11 shrink-0 grid place-items-center bg-muted"><Clock size={18} /></span>
                <div className="w-full">
                  <div className="font-semibold mb-1">Horario</div>
                  <ul className="space-y-0.5 text-sm text-muted-foreground" data-testid="horario-list">
                    {HORARIO.map(([d, h]) => (
                      <li key={d} className="flex justify-between gap-4 max-w-[260px]">
                        <span>{d}</span>
                        <span className={h === "Cerrado" ? "text-muted-foreground/70" : "text-ink font-medium"}>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <button onClick={downloadVCard} data-testid="save-contact-button" className="mt-8 inline-flex items-center gap-3 border border-ink text-ink font-semibold px-6 py-3.5 hover:bg-ink hover:text-white transition-colors duration-300">
              <Download size={18} /> Guardar contacto
            </button>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8 border border-border bg-white p-6 sm:p-10"
          >
            <Tabs defaultValue="cita">
              <TabsList className="grid grid-cols-3 w-full rounded-none bg-muted p-1 mb-8">
                <TabsTrigger value="cita" data-testid="tab-cita" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-white font-semibold">Reservar cita</TabsTrigger>
                <TabsTrigger value="presupuesto" data-testid="tab-presupuesto" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-white font-semibold">Presupuesto</TabsTrigger>
                <TabsTrigger value="mensaje" data-testid="tab-mensaje" className="rounded-none data-[state=active]:bg-ink data-[state=active]:text-white font-semibold">Mensaje</TabsTrigger>
              </TabsList>

              {/* CITA */}
              <TabsContent value="cita">
                <form
                  data-testid="appointment-form"
                  onSubmit={(e) => { e.preventDefault(); post("appointment", "appointments", appt, () => setAppt({ name: "", email: "", phone: "", service: "", date: "", time: "", notes: "" })); }}
                  className="space-y-6"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Nombre" testid="appt-name-field"><Input required value={appt.name} onChange={(e) => setAppt({ ...appt, name: e.target.value })} className={inputCls} data-testid="appt-name-input" /></Field>
                    <Field label="Teléfono" testid="appt-phone-field"><Input required value={appt.phone} onChange={(e) => setAppt({ ...appt, phone: e.target.value })} className={inputCls} data-testid="appt-phone-input" /></Field>
                  </div>
                  <Field label="Email" testid="appt-email-field"><Input type="email" required value={appt.email} onChange={(e) => setAppt({ ...appt, email: e.target.value })} className={inputCls} data-testid="appt-email-input" /></Field>
                  <Field label="Servicio" testid="appt-service-field">
                    <Select value={appt.service} onValueChange={(v) => setAppt({ ...appt, service: v })}>
                      <SelectTrigger className={inputCls} data-testid="appt-service-trigger"><SelectValue placeholder="Elige un servicio" /></SelectTrigger>
                      <SelectContent>{SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Fecha" testid="appt-date-field"><Input type="date" required value={appt.date} onChange={(e) => setAppt({ ...appt, date: e.target.value })} className={inputCls} data-testid="appt-date-input" /></Field>
                    <Field label="Hora" testid="appt-time-field"><Input type="time" required value={appt.time} onChange={(e) => setAppt({ ...appt, time: e.target.value })} className={inputCls} data-testid="appt-time-input" /></Field>
                  </div>
                  <Field label="Notas (opcional)" testid="appt-notes-field"><Textarea value={appt.notes} onChange={(e) => setAppt({ ...appt, notes: e.target.value })} className={`${inputCls} h-24`} data-testid="appt-notes-input" /></Field>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <SubmitBtn loading={loading.appointment} testid="appointment-submit-button">Reservar cita</SubmitBtn>
                    <span className="text-sm text-muted-foreground">¿Tienes dudas? Pregúntanos</span>
                    <a
                      href="https://wa.me/34628328285?text=Hola%20Ventanas%20Hondal%2C%20quiero%20reservar%20una%20cita"
                      target="_blank" rel="noopener noreferrer"
                      data-testid="cita-whatsapp-button"
                      className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-4 hover:brightness-95 transition-[filter] duration-300"
                    >
                      <MessageCircle size={18} /> Por WhatsApp
                    </a>
                  </div>
                </form>
              </TabsContent>

              {/* PRESUPUESTO */}
              <TabsContent value="presupuesto">
                <form
                  data-testid="quote-form"
                  onSubmit={(e) => { e.preventDefault(); post("quote", "quotes", quote, () => setQuote({ name: "", email: "", phone: "", service: "", address: "", description: "" })); }}
                  className="space-y-6"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Nombre" testid="quote-name-field"><Input required value={quote.name} onChange={(e) => setQuote({ ...quote, name: e.target.value })} className={inputCls} data-testid="quote-name-input" /></Field>
                    <Field label="Teléfono" testid="quote-phone-field"><Input required value={quote.phone} onChange={(e) => setQuote({ ...quote, phone: e.target.value })} className={inputCls} data-testid="quote-phone-input" /></Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Email" testid="quote-email-field"><Input type="email" required value={quote.email} onChange={(e) => setQuote({ ...quote, email: e.target.value })} className={inputCls} data-testid="quote-email-input" /></Field>
                    <Field label="Servicio" testid="quote-service-field">
                      <Select value={quote.service} onValueChange={(v) => setQuote({ ...quote, service: v })}>
                        <SelectTrigger className={inputCls} data-testid="quote-service-trigger"><SelectValue placeholder="Elige un servicio" /></SelectTrigger>
                        <SelectContent>{SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <Field label="Dirección / Localidad (opcional)" testid="quote-address-field"><Input value={quote.address} onChange={(e) => setQuote({ ...quote, address: e.target.value })} className={inputCls} data-testid="quote-address-input" /></Field>
                  <Field label="Describe tu proyecto" testid="quote-description-field"><Textarea required value={quote.description} onChange={(e) => setQuote({ ...quote, description: e.target.value })} className={`${inputCls} h-28`} data-testid="quote-description-input" placeholder="Nº de ventanas, medidas aproximadas, material deseado..." /></Field>
                  <SubmitBtn loading={loading.quote} testid="quote-submit-button">Pedir presupuesto</SubmitBtn>
                </form>
              </TabsContent>

              {/* MENSAJE */}
              <TabsContent value="mensaje">
                <form
                  data-testid="contact-form"
                  onSubmit={(e) => { e.preventDefault(); post("contact", "contact", contact, () => setContact({ name: "", email: "", phone: "", message: "" })); }}
                  className="space-y-6"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <Field label="Nombre" testid="contact-name-field"><Input required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={inputCls} data-testid="contact-name-input" /></Field>
                    <Field label="Teléfono (opcional)" testid="contact-phone-field"><Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className={inputCls} data-testid="contact-phone-input" /></Field>
                  </div>
                  <Field label="Email" testid="contact-email-field"><Input type="email" required value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={inputCls} data-testid="contact-email-input" /></Field>
                  <Field label="Mensaje" testid="contact-message-field"><Textarea required value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} className={`${inputCls} h-32`} data-testid="contact-message-input" /></Field>
                  <SubmitBtn loading={loading.contact} testid="contact-submit-button">Enviar mensaje</SubmitBtn>
                </form>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
